import {
  FolderControllerCopyFolderRequest,
  FolderControllerCreateCrateRequest,
  FolderControllerMoveCrateFolderRequest,
  FolderControllerMoveFolderRequest,
  FolderControllerUpdateRequest,
} from '@cratehackers/api-client';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  FilterType,
  CrateType,
  CustomFolder,
  FolderType,
  SelectedFolderItemType,
} from '../../components/myCrates/folder-management/types';
import ApiService from '../../services/Api.service';

export type { CrateType, CustomFolder, FolderType, SelectedFolderItemType };

type FoldersState = {
  folders: FolderType[];
  userCratesByCrateId: Record<string, CrateType>;
  selectedFolders: SelectedFolderItemType[];
  showCheckbox: boolean;
  operationMode: 'move' | 'copy' | null;
  loading: boolean;
};

const initialState: FoldersState = {
  folders: [],
  userCratesByCrateId: {},
  selectedFolders: [],
  showCheckbox: false,
  operationMode: null,
  loading: false,
};

export const fetchFolders = createAsyncThunk<
  FolderType[],
  void,
  { rejectValue: string }
>('folders/fetchFolders', async (_, thunkAPI) => {
  const api = ApiService.getApiClient();

  try {
    let data = await api.folderControllerFindAll();

    if (!data || data.length === 0) {
      data = await api.folderControllerCreateDefaultFolder();
    }

    return data as FolderType[];
  } catch (e) {
    console.error(`Error while fetching folders:`, e);

    return thunkAPI.rejectWithValue('Failed to fetch folders');
  }
});

export const createCrateEntries = createAsyncThunk<
  {
    crates: CrateType[];
    tempCrates: {
      tempId: string;
      targetParentId: string;
    }[];
  },
  {
    crates: {
      crateId: string;
      name: string;
      tempId: string;
    }[];
    index: number;
    targetParentId: string;
    moveFromRoot?: boolean;
  }
>(
  'folders/createCrateEntries',
  async ({ crates, targetParentId, index }, thunkAPI) => {
    try {
      const api = ApiService.getApiClient();

      const req: FolderControllerCreateCrateRequest = {
        createFolderCrateDto: {
          folderId: targetParentId,
          crateIds: crates.map((item) => item.crateId!),
          index,
        },
      };

      const results = await api.folderControllerCreateCrate(req);

      return {
        crates: results as unknown as CrateType[],
        tempCrates: crates.map((c) => ({
          tempId: c.tempId,
          targetParentId,
        })),
      };
    } catch (e) {
      console.error(`Error while copying crates:`, e);
      return thunkAPI.rejectWithValue('Failed to copy crates');
    }
  },
);
export const renameFolder = createAsyncThunk<
  {
    folder: FolderType;
  },
  {
    folderId: string;
    newName: string;
    oldName: string;
  }
>('folders/renameFolder', async ({ folderId, newName }, thunkAPI) => {
  try {
    const api = ApiService.getApiClient();

    const req: FolderControllerUpdateRequest = {
      id: folderId,
      updateFolderDto: {
        name: newName,
      },
    };

    const folder = await api.folderControllerUpdate(req);

    return {
      folder: folder as FolderType,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue('Failed to rename folder');
  }
});

export const copyFolders = createAsyncThunk<
  {
    folders: FolderType[];
    targetParentId: string;
    tempIds: string[];
  },
  {
    folders: {
      folderId: string;
      newName: string;
      tempId: string;
    }[];
    targetParentId: string;
    index: number;
  }
>(
  'folders/copyFolders',
  async ({ folders, targetParentId, index }, thunkAPI) => {
    try {
      const api = ApiService.getApiClient();

      const req: FolderControllerCopyFolderRequest = {
        copyFolderDto: {
          folders: folders.map(({ folderId, newName }) => ({
            folderId,
            newName,
          })),
          newParentId: targetParentId,
          index,
        },
      };

      const result = await api.folderControllerCopyFolder(req);

      return {
        folders: result as unknown as FolderType[],
        tempIds: folders.map((f) => f.tempId),
        targetParentId,
      };
    } catch (e) {
      console.error(`Error while copying folders:`, e);
      return thunkAPI.rejectWithValue('Failed to copy folders');
    }
  },
);

export const moveCrates = createAsyncThunk<
  {
    folders: FolderType[];
    targetParentId: string;
  },
  {
    crates: {
      id: string;
      sourceParentId: string;
    }[];
    index: number;
    targetParentId: string;
  }
>('folders/moveCrates', async ({ crates, targetParentId, index }, thunkAPI) => {
  if (crates.length == 0) throw Error('No Crates');
  try {
    const api = ApiService.getApiClient();
    const req: FolderControllerMoveCrateFolderRequest = {
      moveCrateFolderDto: {
        crateIds: crates.map((item) => item.id),
        folderId: targetParentId,
        index,
      },
    };
    const result = await api.folderControllerMoveCrateFolder(req);

    return {
      folders: result as unknown as FolderType[],
      targetParentId,
    };
  } catch (e) {
    console.error(`Error while moving crates:`, e);
    return thunkAPI.rejectWithValue('Failed to move crates');
  }
});

export const moveFolders = createAsyncThunk<
  {
    folders: FolderType[];
    targetParentId: string;
  },
  {
    folders: {
      id: string;
      sourceParentId: string;
      newName: string;
    }[];
    targetParentId: string;
    index: number;
  },
  {
    rejectValue: string;
  }
>(
  'folders/moveFolders',
  async ({ folders, targetParentId, index }, thunkAPI) => {
    try {
      const api = ApiService.getApiClient();

      const req: FolderControllerMoveFolderRequest = {
        moveFolderDto: {
          folders: folders.map(({ id, newName }) => ({
            folderId: id,
            newName,
          })),
          newParentId: targetParentId,
          index,
        },
      };

      const result = await api.folderControllerMoveFolder(req);

      return {
        folders: result as unknown as FolderType[],
        targetParentId,
      };
    } catch (e) {
      console.error(`Error while moving folders:`, e);

      return thunkAPI.rejectWithValue('Failed to move folders');
    }
  },
);

export const findFolderById = (
  folders: FolderType[],
  id: string,
): FolderType | null => {
  for (const folder of folders) {
    if (folder.id === id) return folder;

    const found = findFolderById(folder.children, id);
    if (found) return found;
  }
  return null;
};
export const findCrateById = (
  folders: FolderType[],
  crateId: string,
): CrateType | null => {
  for (const folder of folders) {
    const crate = folder.crates.find((c) => c.id === crateId);
    if (crate) return crate;

    if (folder.children.length) {
      const found = findCrateById(folder.children, crateId);
      if (found) return found;
    }
  }
  return null;
};

const deleteFolderById = (folders: FolderType[], id: string): FolderType[] => {
  let treeOpenIds: string[] = [];
  const getTreeOpenIds = localStorage.getItem('tree-open-ids');
  if (getTreeOpenIds) treeOpenIds = JSON.parse(getTreeOpenIds);
  return folders
    .filter((f) => f.id !== id)
    .map((f, index, array) => {
      const updatedChildren = deleteFolderById(f.children, id);

      treeOpenIds = treeOpenIds.filter((item) => item !== id);

      if (index === array.length - 1) {
        localStorage.setItem('tree-open-ids', JSON.stringify(treeOpenIds));
      }
      const childrenChanged =
        updatedChildren.length !== f.children.length ||
        updatedChildren.some((child, index) => child !== f.children[index]);

      return {
        ...f,
        children: updatedChildren,
        modifiedAt: childrenChanged ? new Date().toISOString() : f.modifiedAt,
      };
    });
};

export function deleteCrateById(
  folders: FolderType[],
  crateId: string,
): FolderType[] {
  return folders.map((folder) => {
    const originalCratesLength = folder.crates?.length || 0;

    const updatedCrates = (folder.crates || []).filter(
      (crate) => crate.id !== crateId,
    );

    const crateDeleted = updatedCrates.length !== originalCratesLength;

    const updatedChildren = folder.children
      ? deleteCrateById(folder.children, crateId)
      : [];

    const childModified = updatedChildren.some(
      (child, index) => child.modifiedAt !== folder.children[index]?.modifiedAt,
    );

    return {
      ...folder,
      crates: updatedCrates,
      children: updatedChildren,
      modifiedAt:
        crateDeleted || childModified
          ? new Date().toISOString()
          : folder.modifiedAt,
    };
  });
}
export function restoreCrateById(
  folders: FolderType[],
  crate: CrateType,
  folderId: string,
): FolderType[] {
  return folders.map((folder) => {
    const isTargetFolder = folder.id === folderId;

    const updatedChildren = folder.children
      ? restoreCrateById(folder.children, crate, folderId)
      : [];

    const childModified = updatedChildren.some(
      (child, index) => child.modifiedAt !== folder.children[index]?.modifiedAt,
    );

    return {
      ...folder,
      crates: isTargetFolder
        ? [...(folder.crates || []), crate]
        : folder.crates,
      children: updatedChildren,
      modifiedAt:
        isTargetFolder || childModified
          ? new Date().toISOString()
          : folder.modifiedAt,
    };
  });
}

function deleteFolderKeepChildrenById(
  folders: FolderType[],
  folderId: string,
  parent: FolderType | null = null,
): boolean {
  const rootFolderId = '00000000-0000-0000-0000-000000000001';
  for (let i = 0; i < folders.length; i++) {
    const folder = folders[i];

    if (folder.id === folderId) {
      const removed = folders[i];

      folders.splice(i, 1);

      if (removed.children?.length) {
        folders.splice(i, 0, ...removed.children);
      }

      if (parent && removed.crates?.length && parent.id !== rootFolderId) {
        parent.crates.push(...removed.crates);
      }

      return true;
    }

    if (deleteFolderKeepChildrenById(folder.children, folderId, folder)) {
      return true;
    }
  }

  return false;
}
const traverseFolders = (
  folders: FolderType[],
  callback: (folder: FolderType) => void,
) => {
  folders.forEach((folder) => {
    callback(folder);
    traverseFolders(folder.children, callback);
  });
};
function removeFolderInPlace(
  folders: FolderType[],
  folderId: string,
): FolderType | null {
  for (let i = 0; i < folders.length; i++) {
    const f = folders[i];

    if (f.id === folderId) {
      return folders.splice(i, 1)[0];
    }

    const found = removeFolderInPlace(f.children, folderId);
    if (found) return found;
  }

  return null;
}

function removeCrateInPlace(
  folders: FolderType[],
  id: string,
): {
  crate: { id: string; name: string; crateId: string } | null;
  sourceFolder: FolderType | null;
} {
  for (const folder of folders) {
    const index = folder.crates.findIndex((c) => c.id === id);
    if (index !== -1) {
      const [crate] = folder.crates.splice(index, 1);
      return { crate, sourceFolder: folder };
    }

    const result = removeCrateInPlace(folder.children, id);
    if (result.crate) return result;
  }

  return { crate: null, sourceFolder: null };
}

function isDescendant(parent: FolderType, id: string): boolean {
  if (!parent.children) return false;

  for (const child of parent.children) {
    if (child.id === id) return true;
    if (isDescendant(child, id)) return true;
  }

  return false;
}

const collectAllIds = (folder: FolderType): string[] => {
  let ids: string[] = [folder.id];

  ids.push(...folder.crates.map((c) => c.id));

  for (const child of folder.children) {
    ids = ids.concat(collectAllIds(child));
  }

  return ids;
};
export const findParentPath = (
  folders: FolderType[],
  targetId: string,
  path: FolderType[] = [],
): FolderType[] | null => {
  for (const folder of folders) {
    if (folder.crates.some((c) => c.id === targetId)) {
      return [...path, folder];
    }

    if (folder.id === targetId) {
      return path;
    }

    const found = findParentPath(folder.children, targetId, [...path, folder]);

    if (found) return found;
  }

  return null;
};

export const findParentPathInString = (
  folders: FolderType[],
  targetId: string,
  path: string[] = [],
): string | null => {
  for (const folder of folders) {
    const currentPath = [...path, folder.name];

    if (folder.crates.some((c) => c.id === targetId)) {
      return currentPath.join(' / ');
    }

    if (folder.id === targetId) {
      return path.join(' / ');
    }

    const found = findParentPathInString(
      folder.children,
      targetId,
      currentPath,
    );

    if (found) return found;
  }

  return null;
};

export const findParentId = (folders: FolderType[], id: string) => {
  const sourcePath = findParentPath(folders, id);
  const sourceParentId = sourcePath?.[sourcePath.length - 1]?.id;
  return sourceParentId;
};

const ROOT_FOLDER_ID = '00000000-0000-0000-0000-000000000001';

export const collectFolderedCrateIds = (folders: FolderType[]): Set<string> => {
  const ids = new Set<string>();

  const traverse = (folderList: FolderType[]) => {
    for (const folder of folderList) {
      if (folder.id !== ROOT_FOLDER_ID) {
        for (const crate of folder.crates) {
          ids.add(crate.crateId);
        }
      }
      traverse(folder.children);
    }
  };

  traverse(folders);
  return ids;
};

const applyUserCratesToRoot = (state: FoldersState) => {
  const root = state.folders[0];
  if (!root || Object.keys(state.userCratesByCrateId).length === 0) return;

  const folderedCrateIds = collectFolderedCrateIds(state.folders);
  root.crates = Object.values(state.userCratesByCrateId)
    .filter((crate) => !folderedCrateIds.has(crate.crateId))
    .sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, {
        sensitivity: 'base',
      }),
    );
};

const isFolderFullySelected = (
  folder: FolderType,
  selected: string[],
): boolean => {
  const allCratesSelected = folder.crates.every((c) => selected.includes(c.id));

  const allChildrenSelected = folder.children.every((child) =>
    isFolderFullySelected(child, selected),
  );

  return allCratesSelected && allChildrenSelected;
};

const myCratesFoldersSlice = createSlice({
  name: 'myCratesFolders',
  initialState,
  reducers: {
    addMyCratesFolder: (
      state,
      action: PayloadAction<{
        id: string;
        name: string;
        parentId?: string;
        isCustom?: 'folder-filter' | 'crate-filter' | null;
        customFolder?: CustomFolder;
      }>,
    ) => {
      const {
        id,
        name,
        parentId,
        isCustom = null,
        customFolder,
      } = action.payload;

      const newFolder: FolderType = {
        id,
        name,
        crates: [],
        children: [],
        createdAt: new Date().toISOString(),
        isCustom,
        customFolder,
      };

      if (!parentId) {
        state.folders.push(newFolder);
      } else {
        const parent = findFolderById(state.folders, parentId);
        if (parent) {
          parent.children.push(newFolder);
        }
      }
    },
    deleteMyCratesFolder: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((id) => {
        state.folders = deleteFolderById(state.folders, id);
      });
    },

    deleteMyCrate: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((id) => {
        const junctionCrate = findCrateById(state.folders, id);
        const root = state.folders[0];

        if (
          junctionCrate &&
          root &&
          junctionCrate.id !== junctionCrate.crateId
        ) {
          const alreadyAtRoot = root.crates.some(
            (c) => c.crateId === junctionCrate.crateId,
          );
          if (!alreadyAtRoot) {
            root.crates.push({
              ...junctionCrate,
              id: junctionCrate.crateId,
            });
          }
        }

        state.folders = deleteCrateById(state.folders, id);
      });
    },
    deleteFolderKeepChildren: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((id) => {
        deleteFolderKeepChildrenById(state.folders, id);
      });
    },

    removeCratesFromFolders: (
      state,
      action: PayloadAction<{ folderIds: string[]; itemIds: string[] }>,
    ) => {
      const removeSet = new Set(action.payload.itemIds);

      action.payload.folderIds.forEach((folderId) => {
        const folder = findFolderById(state.folders, folderId);
        if (!folder) return;
        folder.modifiedAt = new Date().toISOString();
        folder.crates = folder.crates.filter(
          (crate) => !removeSet.has(crate.id),
        );
      });
    },

    removeCratesFromAllFolders: (
      state,
      action: PayloadAction<{ crateIds: string[] }>,
    ) => {
      const removeSet = new Set(action.payload.crateIds);

      traverseFolders(state.folders, (folder) => {
        folder.crates = folder.crates.filter(
          (crate) => !removeSet.has(crate.crateId),
        );
      });
    },

    updateFolderColor: (
      state,
      action: PayloadAction<{ folderId: string; color: string }>,
    ) => {
      const { color, folderId } = action.payload;
      const folder = findFolderById(state.folders, folderId);
      if (folder) {
        folder.color = color;
        folder.modifiedAt = new Date().toISOString();
      }
    },
    updateFolderTags: (
      state,
      action: PayloadAction<{ folderId: string; tags: string[] }>,
    ) => {
      const { tags, folderId } = action.payload;
      const folder = findFolderById(state.folders, folderId);
      if (folder) {
        folder.tags = tags;
        folder.modifiedAt = new Date().toISOString();
      }
    },

    setRootCrates: (state, action: PayloadAction<CrateType[]>) => {
      for (const crate of action.payload) {
        state.userCratesByCrateId[crate.crateId] = crate;
      }
      applyUserCratesToRoot(state);
    },

    toggleSelectedFolder: (
      state,
      action: PayloadAction<{
        id: string;
        name: string;
        crateId?: string;
      }>,
    ) => {
      const item = action.payload;
      const id = item.id;

      const folder = findFolderById(state.folders, id);

      if (folder) {
        const ids = collectAllIds(folder);

        const isSelected = state.selectedFolders.some((f) => f.id === id);

        if (isSelected) {
          state.selectedFolders = state.selectedFolders.filter(
            (f) => f.id !== id,
          );
        } else {
          state.selectedFolders.push(item);

          state.selectedFolders = state.selectedFolders.filter(
            (f) => !ids.includes(f.id) || f.id === id,
          );
        }

        return;
      }

      const exists = state.selectedFolders.some((f) => f.id === id);

      if (exists) {
        state.selectedFolders = state.selectedFolders.filter(
          (f) => f.id !== id,
        );
      } else {
        state.selectedFolders.push(item);
      }
    },

    clearSelection: (state) => {
      state.selectedFolders = [];
    },
    addtoSelectedFolders: (
      state,
      action: PayloadAction<{
        id: string;
        name: string;
        crateId?: string;
      }>,
    ) => {
      state.selectedFolders = [];
      const folder = action.payload;
      state.selectedFolders.push(folder);
    },

    toggleShowCheckbox: (state) => {
      state.showCheckbox = !state.showCheckbox;
    },

    setOperationMode: (
      state,
      action: PayloadAction<{ mode: 'copy' | 'move' | null }>,
    ) => {
      state.operationMode = action.payload.mode;
    },

    addEditableFolder: (state, action: PayloadAction<{ parentId: string }>) => {
      const { parentId } = action.payload;

      const parent = findFolderById(state.folders, parentId);
      if (!parent) return;

      const exists = parent.children.some((child) => child.id === 'Editable');

      if (exists) return;

      parent.children.unshift({
        id: 'Editable',
        name: '',
        crates: [],
        children: [],
      });
    },
    removeEditableFolder: (
      state,
      action: PayloadAction<{ parentId: string }>,
    ) => {
      const { parentId } = action.payload;

      const parent = findFolderById(state.folders, parentId);
      if (!parent) return;

      parent.children = parent.children.filter(
        (child) => child.id !== 'Editable',
      );
    },
    updateCustomFolderFilters: (
      state,
      action: PayloadAction<{
        id: string;
        filters: FilterType;
      }>,
    ) => {
      const folder = findFolderById(state.folders, action.payload.id);

      if (folder?.customFolder) {
        folder.customFolder.filters = action.payload.filters;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFolders.pending, (state) => {
        state.loading = true;
      })

      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.loading = false;
        state.folders = action.payload;
        applyUserCratesToRoot(state);
      })

      .addCase(fetchFolders.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createCrateEntries.pending, (state, action) => {
        const { crates, targetParentId, index, moveFromRoot } = action.meta.arg;

        const parent = findFolderById(state.folders, targetParentId);
        if (!parent) return;

        crates.forEach(({ crateId, name, tempId }) => {
          parent.crates.splice(index, 0, {
            id: tempId,
            crateId,
            name,
          });
        });
        parent.modifiedAt = new Date().toISOString();

        if (moveFromRoot) {
          const root = state.folders[0];
          if (!root) return;
          const movedIds = new Set(crates.map((c) => c.crateId));
          root.crates = root.crates.filter((c) => !movedIds.has(c.crateId));
        }
      })

      .addCase(createCrateEntries.fulfilled, (state, action) => {
        const { crates, tempCrates } = action.payload;

        tempCrates.forEach((temp, i) => {
          const parent = findFolderById(state.folders, temp.targetParentId);

          if (!parent) return;

          const index = parent.crates.findIndex((c) => c.id === temp.tempId);

          if (index !== -1) {
            parent.crates[index] = crates[i];
          }
        });
      })

      .addCase(createCrateEntries.rejected, (state, action) => {
        const { crates, targetParentId, moveFromRoot } = action.meta.arg;

        const parent = findFolderById(state.folders, targetParentId);
        if (!parent) return;

        const tempIds = crates.map((c) => c.tempId);

        parent.crates = parent.crates.filter((c) => !tempIds.includes(c.id));

        if (moveFromRoot) {
          const root = state.folders[0];
          if (!root) return;

          crates.forEach(({ crateId, name }) => {
            const exists = root.crates.some((c) => c.crateId === crateId);
            if (!exists) {
              root.crates.push({
                id: crateId,
                crateId,
                name,
              });
            }
          });
        }
      })
      .addCase(copyFolders.pending, (state, action) => {
        const { folders, targetParentId, index } = action.meta.arg;

        const cloneFolder = (
          folder: FolderType,
          idOverride?: string,
        ): FolderType => ({
          ...folder,
          id: idOverride ?? crypto.randomUUID(),
          crates: folder.crates.map((crate) => ({
            ...crate,
            id: crypto.randomUUID(),
          })),
          children: folder.children.map((child) => cloneFolder(child)),
        });

        const clonedFolders = folders
          .map((item) => {
            const sourceFolder = findFolderById(state.folders, item.folderId);

            if (!sourceFolder) return null;

            const clonedFolder = cloneFolder(sourceFolder, item.tempId);

            clonedFolder.name = item.newName;
            clonedFolder.modifiedAt = new Date().toISOString();

            return clonedFolder;
          })
          .filter(Boolean) as FolderType[];

        if (!targetParentId) {
          state.folders.splice(index, 0, ...clonedFolders);
          return;
        }

        const parent = findFolderById(state.folders, targetParentId);

        if (!parent) return;

        parent.modifiedAt = new Date().toISOString();
        parent.children.splice(index, 0, ...clonedFolders);
      })
      .addCase(copyFolders.fulfilled, (state, action) => {
        const { folders: copiedFolders, targetParentId } = action.payload;

        const requestFolders = action.meta.arg.folders;

        if (!targetParentId) {
          copiedFolders.forEach((folder, i) => {
            const index = state.folders.findIndex(
              (f) => f.id === requestFolders[i].tempId,
            );

            if (index !== -1) {
              state.folders[index] = folder;
            }
          });

          return;
        }

        const parent = findFolderById(state.folders, targetParentId);

        if (!parent) return;

        copiedFolders.forEach((folder, i) => {
          const index = parent.children.findIndex(
            (c) => c.id === requestFolders[i].tempId,
          );

          if (index !== -1) {
            parent.children[index] = folder;
          }
        });
      })
      .addCase(copyFolders.rejected, (state, action) => {
        const { targetParentId, folders } = action.meta.arg;

        const tempIds = folders.map((folder) => folder.tempId);

        if (!targetParentId) {
          state.folders = state.folders.filter((f) => !tempIds.includes(f.id));

          return;
        }

        const parent = findFolderById(state.folders, targetParentId);

        if (!parent) return;

        parent.children = parent.children.filter(
          (c) => !tempIds.includes(c.id),
        );
      })
      .addCase(moveCrates.pending, (state, action) => {
        const { crates, targetParentId, index } = action.meta.arg;

        if (!targetParentId) return;

        const targetParent = findFolderById(state.folders, targetParentId);
        if (!targetParent) return;

        crates.forEach(({ id }) => {
          const { crate } = removeCrateInPlace(state.folders, id);

          if (!crate) return;

          const alreadyExists =
            targetParent.crates?.some(
              (item) => item.crateId === crate.crateId,
            ) ?? false;

          // if (alreadyExists) {
          //   return;
          // }

          targetParent.crates.splice(index, 0, crate);
        });

        targetParent.modifiedAt = new Date().toISOString();
      })
      .addCase(moveCrates.rejected, (state, action) => {
        if (!action.payload) return;

        const { crates } = action.meta.arg;

        crates.forEach(({ id, sourceParentId }) => {
          if (!sourceParentId) return;

          const { crate } = removeCrateInPlace(state.folders, id);

          if (!crate) return;

          const sourceParent = findFolderById(state.folders, sourceParentId);

          if (!sourceParent) return;

          const alreadyExists =
            sourceParent.crates?.some(
              (item) => item.crateId === crate.crateId,
            ) ?? false;

          if (alreadyExists) {
            return;
          }

          sourceParent.crates.splice(0, 0, crate);
        });
      })
      .addCase(moveFolders.pending, (state, action) => {
        const { folders, targetParentId, index } = action.meta.arg;

        folders.forEach(({ id, newName }) => {
          if (id === targetParentId) return;

          const movedFolder = removeFolderInPlace(state.folders, id);

          if (!movedFolder) return;

          if (isDescendant(movedFolder, targetParentId)) return;

          movedFolder.name = newName;
          movedFolder.modifiedAt = new Date().toISOString();

          if (!targetParentId) {
            state.folders.splice(index, 0, movedFolder);
            return;
          }

          const parent = findFolderById(state.folders, targetParentId);

          if (!parent) return;

          parent.modifiedAt = new Date().toISOString();
          parent.children.splice(index, 0, movedFolder);
        });
      })
      .addCase(moveFolders.rejected, (state, action) => {
        if (!action.payload) return;

        const { folders } = action.meta.arg;

        folders.forEach(({ id, sourceParentId }) => {
          const movedFolder = removeFolderInPlace(state.folders, id);

          if (!movedFolder) return;

          movedFolder.modifiedAt = new Date().toISOString();

          if (!sourceParentId) {
            state.folders.splice(0, 0, movedFolder);
            return;
          }

          const sourceParent = findFolderById(state.folders, sourceParentId);

          if (!sourceParent) return;

          sourceParent.modifiedAt = new Date().toISOString();

          sourceParent.children.splice(0, 0, movedFolder);
        });
      })
      .addCase(renameFolder.pending, (state, action) => {
        if (!action.payload) return;

        const { folderId, newName } = action.meta.arg;
        const folder = findFolderById(state.folders, folderId);
        if (folder) {
          folder.name = newName;
          folder.modifiedAt = new Date().toISOString();
        }
      })
      .addCase(renameFolder.rejected, (state, action) => {
        if (!action.payload) return;

        const { folderId, oldName } = action.meta.arg;
        const folder = findFolderById(state.folders, folderId);
        if (folder) {
          folder.name = oldName;
          folder.modifiedAt = new Date().toISOString();
        }
      });
  },
});

export const {
  addMyCratesFolder,
  deleteMyCratesFolder,
  deleteMyCrate,
  removeCratesFromFolders,
  removeCratesFromAllFolders,
  deleteFolderKeepChildren,
  setRootCrates,
  toggleSelectedFolder,
  addtoSelectedFolders,
  clearSelection,
  toggleShowCheckbox,
  addEditableFolder,
  removeEditableFolder,
  setOperationMode,
  updateFolderColor,
  updateCustomFolderFilters,
  updateFolderTags,
} = myCratesFoldersSlice.actions;

export default myCratesFoldersSlice.reducer;
